/*
 * This module owns a request header on the shared axios defaults, exactly as
 * `services/AuthService` owns `Authorization`. There is no typed helper for
 * setting a default header, so the raw import is the only door.
 */
// eslint-disable-next-line no-restricted-imports
import axios from "axios";
import { useSyncExternalStore } from "react";

/**
 * The location an anonymous visitor chose to browse. Pricing is per location,
 * so once anonymous checkout is on the backend needs to know which one the
 * visitor is looking at — there is no account to read it from. The choice
 * lives in `sessionStorage` (same lifetime as the pending cart and the
 * post-login return path) and rides along on every request as a header.
 */

const BROWSE_LOCATION_KEY = "anonymousBrowsing.locationId";

export const BROWSE_LOCATION_HEADER = "X-RepeatMD-Browse-Location";

const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};

/**
 * Safari's private mode throws on any `sessionStorage` access. The browse
 * location is a convenience, not a correctness input, so a storage-less
 * session simply has no remembered location.
 */
const readStoredLocationId = (): string | null => {
  try {
    return sessionStorage.getItem(BROWSE_LOCATION_KEY);
  } catch {
    return null;
  }
};

const applyHeader = (locationId: string | null) => {
  if (locationId) axios.defaults.headers.common[BROWSE_LOCATION_HEADER] = locationId;
  else delete axios.defaults.headers.common[BROWSE_LOCATION_HEADER];
};

export const getBrowseLocationId = (): string | null => readStoredLocationId();

/** Persists the choice and puts it on (or takes it off) every outgoing request. */
export const setBrowseLocationId = (locationId: string | null): void => {
  try {
    if (locationId) sessionStorage.setItem(BROWSE_LOCATION_KEY, locationId);
    else sessionStorage.removeItem(BROWSE_LOCATION_KEY);
  } catch {
    // See `readStoredLocationId`: the header below still holds for this page.
  }
  applyHeader(locationId);
  notify();
};

/**
 * Re-applies the stored choice to the axios defaults on app start, where the
 * `sessionStorage` entry outlives the module-level axios instance.
 */
export const restoreBrowseLocationHeader = (): void => {
  applyHeader(readStoredLocationId());
};

export const initializeBrowseLocationFromUrl = (): void => {
  const linkedLocation = new URLSearchParams(window.location.search).get("browseLocation");
  if (
    linkedLocation &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(linkedLocation)
  ) {
    // The API validates that this location is active and belongs to the current practice.
    setBrowseLocationId(linkedLocation);
    return;
  }
  applyHeader(readStoredLocationId());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useBrowseLocationId = (): string | null =>
  useSyncExternalStore(subscribe, getBrowseLocationId, () => null);
